// src/components/pages/Companies.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import storageService from '../../services/storageService';
import './Companies.css';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    status: 'Active',
    website: '',
    description: '',
    foundedYear: ''
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = () => {
    const allCompanies = storageService.getCompanies();
    setCompanies(allCompanies);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingCompany) {
      storageService.updateCompany(editingCompany.id, formData);
    } else {
      storageService.createCompany(formData);
    }
    
    resetForm();
    loadCompanies();
    setShowModal(false);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData(company);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      storageService.deleteCompany(id);
      loadCompanies();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      industry: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      status: 'Active',
      website: '',
      description: '',
      foundedYear: ''
    });
    setEditingCompany(null);
  };

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = 
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesIndustry = filterIndustry === 'all' || company.industry === filterIndustry;
    const matchesStatus = filterStatus === 'all' || company.status === filterStatus;
    
    return matchesSearch && matchesIndustry && matchesStatus;
  });

  // Get unique industries and stats
  const industries = ['all', ...new Set(companies.map(c => c.industry).filter(Boolean))];
  const activeCompanies = companies.filter(c => c.status === 'Active').length;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading companies...</p>
      </div>
    );
  }

  return (
    <div className="companies-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Companies</h1>
          <p>Manage client companies and their details</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <span className="btn-icon">+</span> Add Company
        </button>
      </div>

      {/* Stats */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon">🏢</div>
          <div className="stat-content">
            <h3>{companies.length}</h3>
            <p>Total Companies</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{activeCompanies}</h3>
            <p>Active Companies</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{industries.length - 1}</h3>
            <p>Industries</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search companies by name, contact, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterIndustry} 
            onChange={(e) => setFilterIndustry(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Industries</option>
            {industries.filter(i => i !== 'all').map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
          
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Companies Table */}
      <div className="table-container">
        {filteredCompanies.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Industry</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map(company => (
                <tr key={company.id}>
                  <td>
                    <div className="company-cell">
                      <div className="company-avatar">
                        {company.name.charAt(0)}
                      </div>
                      <div className="company-info">
                        <Link to={`/company/${company.id}/jobs`} className="company-name">
                          {company.name}
                        </Link>
                        <span className="company-website">{company.website || 'No website'}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="industry-badge">{company.industry || 'N/A'}</span>
                  </td>
                  <td>{company.contactPerson || 'N/A'}</td>
                  <td>
                    <a href={`mailto:${company.email}`} className="email-link">
                      {company.email || 'N/A'}
                    </a>
                  </td>
                  <td>{company.phone || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${company.status === 'Active' ? 'active' : 'inactive'}`}>
                      {company.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => handleEdit(company)} 
                        className="action-btn edit"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <Link 
                        to={`/company/${company.id}/jobs`} 
                        className="action-btn view"
                        title="View Jobs"
                      >
                        👁️
                      </Link>
                      <button 
                        onClick={() => handleDelete(company.id)} 
                        className="action-btn delete"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h3>No companies found</h3>
            <p>Try adding a new company or adjust your search filters</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              Add Your First Company
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingCompany ? 'Edit Company' : 'Add New Company'}</h2>
              <button onClick={() => { resetForm(); setShowModal(false); }} className="modal-close">
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter company name"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Industry *</label>
                    <input
                      type="text"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      placeholder="e.g., Technology, Finance"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Contact Person *</label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      placeholder="Enter contact person name"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="contact@company.com"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 9876543210"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://company.com"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Founded Year</label>
                    <input
                      type="number"
                      name="foundedYear"
                      value={formData.foundedYear}
                      onChange={handleInputChange}
                      placeholder="e.g., 2010"
                      min="1900"
                      max="2024"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Enter company address"
                    className="form-textarea"
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Brief description about the company"
                    className="form-textarea"
                  />
                </div>
              </div>
              
              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  {editingCompany ? 'Update Company' : 'Add Company'}
                </button>
                <button 
                  type="button" 
                  onClick={() => { resetForm(); setShowModal(false); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;